import { create } from 'zustand'
import { apiService } from '../services/api'
import { useAuthStore } from './authStore'

// Mirrors the Angular client-exercise flow (thfnewweb-main/clientexercise):
//  - the API returns plans -> epe[]; we flatten them into one list of `items`
//  - editing a value saves it right away: manual draft -> createmanualentry,
//    planned exercise not logged yet -> create, otherwise -> updatelogdata

// API date format is YYYY-M-D (not zero padded)
export const formatDateForAPI = (date) => {
  const d = new Date(date)
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

// API dates ("2025-3-9" or "2025-03-09") -> local Date
export const parseAPIDate = (value) => {
  const [y, m, d] = String(value || '').split('T')[0].split('-').map(Number)
  return y && m && d ? new Date(y, m - 1, d) : null
}

export const isCardio = (item) => Number(item?.cat_id) === 1

const blank = (value) => (value === 0 || value == null ? '' : value)
const newSet = () => ({ reps: 0, rest: 0, speed: 0, log_reps: '', log_weight: '', isnewset: true, clientnote: '' })
const toNumber = (value) => (Number.isNaN(parseFloat(value)) ? 0 : value)

const normalizeItem = (item, planName) => {
  const next = { ...item, plan_name: item.plan_name || planName }
  if (isCardio(item)) {
    next.log_heartrate = blank(item.log_heartrate)
    next.log_duration = blank(item.log_duration)
  } else {
    const sets = Array.isArray(item.setdetails) ? item.setdetails : []
    next.setdetails = sets.length
      ? sets.map((s) => ({ ...s, log_reps: blank(s.log_reps), log_weight: blank(s.log_weight) }))
      : [newSet()]
  }
  return next
}

const keyOf = (item) =>
  item.logexercisesingle_id ? `l${item.logexercisesingle_id}` : item.exerciseplanexercise_id ? `p${item.exerciseplanexercise_id}` : null

// After a save we reload the day; keep rows where the user had them instead of the server's order.
// The newly saved row takes the slot it was edited in, other unsaved drafts are carried over.
const keepOrder = (previous, savedIndex, next) => {
  const positions = new Map(previous.map((item, i) => [keyOf(item), i]))
  const ranked = next.map((item) => ({ item, pos: positions.get(keyOf(item)) ?? savedIndex }))
  previous.forEach((item, i) => {
    if (item.isManualEntry && i !== savedIndex) ranked.push({ item, pos: i })
  })
  return ranked.sort((a, b) => a.pos - b.pos).map((r) => r.item)
}

// Local row for an exercise picked from the library; saved on the first value entered
const draftFromTemplate = (template, planName) => {
  const cardio = Number(template.exercisetemplatecategory_id) === 1
  return {
    isManualEntry: true,
    clientadded: 1,
    exercisetemplate_id: template.id,
    cat_id: template.exercisetemplatecategory_id,
    plan_name: planName,
    name: template.name,
    description: template.description,
    video_url: template.video_url,
    ...(cardio
      ? { log_heartrate: '', log_duration: '' }
      : { setdetails: [{ reps: 0, rest: 0, speed: 0, log_reps: '', log_weight: '' }] })
  }
}

const companyUrl = () => useAuthStore.getState().companyUrl

const initialState = {
  programs: [],
  clientDetails: null,
  programId: null,
  date: new Date(),
  items: [],
  planName: '',
  error: '',
  loading: false,
  busy: false
}

let loadToken = 0

export const useExerciseStore = create((set, get) => {
  const patchItem = (index, fn) =>
    set((state) => ({ items: state.items.map((item, i) => (i === index ? fn(item) : item)) }))

  const patchSet = (index, setIndex, patch) =>
    patchItem(index, (item) => ({
      ...item,
      setdetails: item.setdetails.map((s, j) => (j === setIndex ? { ...s, ...patch } : s))
    }))

  return {
    ...initialState,

    loadPrograms: async () => {
      const res = await apiService.post(`/${companyUrl()}/getclientprograms`, {})
      if (!res.success) return
      set({ programs: res.data.programs || [], clientDetails: res.data.clientDetais || null })
    },

    // Angular: changing the date lets the server pick the program (program_id ''),
    // changing the program loads it on its own start date.
    loadExercises: async ({ programId = '', date = get().date, saved } = {}) => {
      const token = ++loadToken
      set({ loading: true, date })
      const res = await apiService.post(`/${companyUrl()}/logexercisesingle`, {
        program_id: programId,
        logdate: formatDateForAPI(date)
      })
      if (token !== loadToken) return
      const result = Array.isArray(res.data) ? res.data[0] : res.data
      if (!res.success || !result) {
        set({ loading: false, items: [], error: res.error || 'No exercises found' })
        return
      }
      const plans = result.plans || []
      const loaded = plans.flatMap((plan) => (plan.epe || []).map((ex) => normalizeItem(ex, plan.plan_name)))
      set({
        loading: false,
        error: '',
        programId: result.program_id,
        planName: plans.length ? plans[plans.length - 1].plan_name : '',
        items: saved ? keepOrder(saved.items, saved.index, loaded) : loaded
      })
    },

    // Exercise library search (returns [] on any failure)
    searchExercises: async (text) => {
      const res = await apiService.post(`/${companyUrl()}/searchexercises`, { exercise: text })
      return res.success && Array.isArray(res.data) ? res.data : []
    },

    addDraft: (template) =>
      set((state) => ({ items: [...state.items, draftFromTemplate(template, state.items.at(-1)?.plan_name || state.planName)] })),

    addSet: (index) =>
      patchItem(index, (item) => {
        const { reps, rest, speed } = item.setdetails.at(-1)
        return { ...item, setdetails: [...item.setdetails, { reps, rest, speed, log_reps: '', log_weight: '', isnewset: true }] }
      }),

    // Set one value on a row and persist it (Angular: onAction -> saveExerciseData).
    // `setIndex` is ignored for cardio rows, which store their values on the item itself.
    logValue: async (index, setIndex, field, value) => {
      if (get().busy) return false
      if (isCardio(get().items[index])) patchItem(index, (item) => ({ ...item, [field]: value }))
      else patchSet(index, setIndex, { [field]: value })

      const { items, programId, date } = get()
      const item = items[index]
      const cardio = isCardio(item)
      const logdate = formatDateForAPI(date)
      const cardioData = { log_heartrate: toNumber(item.log_heartrate), log_duration: toNumber(item.log_duration) }

      // Draft or not-yet-logged plan exercise: create the log, then reload the day
      if (item.isManualEntry || !item.logexercisesingle_id) {
        set({ busy: true })
        const res = item.isManualEntry
          ? await apiService.createManualExerciseEntry(companyUrl(), {
              logdate,
              exercisetemplate_id: item.exercisetemplate_id,
              cat_id: item.cat_id,
              plan_name: item.plan_name || get().planName,
              name: item.name,
              sets: 1,
              clientadded: 1,
              program_id: programId,
              data: cardio ? [cardioData] : item.setdetails
            })
          : await apiService.createLogExerciseSingle(companyUrl(), {
              logdate,
              program_id: programId,
              clientadded: 1,
              exerciseplanexercise_id: item.exerciseplanexercise_id,
              data: cardio ? [cardioData] : item.setdetails
            })
        set({ busy: false })
        if (res.success) await get().loadExercises({ programId, date, saved: { items, index } })
        return res.success
      }

      // Already logged: update just the edited set (or the cardio values)
      const row = cardio ? null : item.setdetails[setIndex]
      const payload = cardio
        ? { logexercisesingle_id: item.logexercisesingle_id, data: cardioData }
        : row.isnewset
          ? { data: row, isnewset: true, setiteration: item.setdetails.length, logexercisesingle_id: item.logexercisesingle_id }
          : { data: row, logexercisesingleset_id: row.logexercisesingleset_id }
      if (row?.isnewset) set({ busy: true })
      const res = await apiService.updateLogExerciseSingle(companyUrl(), payload)
      set({ busy: false })
      if (res.success && row?.isnewset) {
        patchSet(index, setIndex, {
          isnewset: false,
          clientadded: 1,
          logexercisesingleset_id: res.data?.data?.logexercisesingleset_id
        })
      }
      return res.success
    },

    saveNote: async (index, clientnote) => {
      const item = get().items[index]
      const res = await apiService.updateLogExerciseSingle(companyUrl(), {
        data: { clientnote },
        logexercisesingle_id: item.logexercisesingle_id
      })
      if (res.success) patchItem(index, (it) => ({ ...it, clientnote }))
      return res.success
    },

    // Unsaved rows are only dropped locally
    deleteSet: async (index, setIndex) => {
      const item = get().items[index]
      const setId = item.setdetails[setIndex].logexercisesingleset_id
      if (setId) {
        const res = await apiService.deleteLogExerciseSingleSet(companyUrl(), setId)
        if (!res.success) return false
      }
      patchItem(index, (it) => ({ ...it, setdetails: it.setdetails.filter((_, j) => j !== setIndex) }))
      return true
    },

    deleteExercise: async (index) => {
      const id = get().items[index].logexercisesingle_id
      if (id) {
        const res = await apiService.deleteLogExerciseSingle(companyUrl(), id)
        if (!res.success) return false
      }
      set((state) => ({ items: state.items.filter((_, i) => i !== index) }))
      return true
    },

    clearAllData: () => {
      loadToken += 1
      set({ ...initialState, date: new Date() })
    }
  }
})
